<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';

    const apiBase = getApiBase();
    let groupedRequests = {};
    let loading = true;
    let showHandledRequests = false;

    // map of requestId → boolean (expanded?)
    let expandedMap = {};

    // Reactive statements to separate unhandled and handled requests
    $: unhandledRequestsByDay = {};
    $: handledRequestsByDay = {};
    $: {
        // Reset the objects
        unhandledRequestsByDay = {};
        handledRequestsByDay = {};
        
        // Populate them based on current groupedRequests
        Object.entries(groupedRequests).forEach(([day, requests]) => {
            const unhandledRequests = requests.filter(req => !req.handled);
            const handledRequests = requests.filter(req => req.handled);
            
            if (unhandledRequests.length > 0) {
                unhandledRequestsByDay[day] = unhandledRequests;
            }
            if (handledRequests.length > 0) {
                handledRequestsByDay[day] = handledRequests;
            }
        });
    }

    async function handleFetchRequests() {
      const token = localStorage.getItem('jwtToken');
      try {
        const res = await fetch(
          `${apiBase}/studentplayground/api/requests/admin-class-registration/`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type':  'application/json'
            }
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        groupedRequests = await res.json();
      } catch (err) {
        console.error('Error fetching requests:', err);
        alert(`Error loading requests: ${err.message}`);
      } finally {
        loading = false;
      }
    }

    onMount(handleFetchRequests);

    // toggle expanded state for one card
    function toggleExpand(id) {
      expandedMap[id] = !expandedMap[id];
      // reassign to trigger Svelte reactivity
      expandedMap = { ...expandedMap };
    }

    function formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      // Format in Lithuania timezone
      return `${d.toLocaleDateString('lt-LT', { timeZone: 'Europe/Vilnius' })} at ${d.toLocaleTimeString('lt-LT', { 
        timeZone: 'Europe/Vilnius',
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }

    function getStudentName(student) {
      return student || 'Unknown Student';
    }

    function getSubjectDisplayName(subject) {
      const subjectNames = {
        "Math": "Mathematics",
        "English": "English Language", 
        "Lithuanian": "Lithuanian Language",
        "History": "History",
        "Chemistry": "Chemistry",
        "Physics": "Physics",
        "Biology": "Biology",
        "IT": "Information Technology"
      };
      return subjectNames[subject] || subject;
    }

    function getGradeDisplayName(grade) {
      if (grade === 'university') {
        return 'University Level';
      }
      return `Grade ${grade}`;
    }

    function formatRequestedTimes(requestedTimes) {
      if (!requestedTimes || requestedTimes.length === 0) {
        return 'No times specified';
      }
      return requestedTimes.map(time => {
        if (!time) return 'Invalid time';
        const d = new Date(time);
        // Format in Lithuania timezone
        return `${d.toLocaleDateString('lt-LT', { timeZone: 'Europe/Vilnius' })} at ${d.toLocaleTimeString('lt-LT', { 
          timeZone: 'Europe/Vilnius',
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      }).join(', ');
    }

    // Function to handle admin handled/unhandled actions
    async function handleAdminAction(requestId, action) {
      const token = localStorage.getItem('jwtToken');
      
      try {
        const response = await fetch(
          `${apiBase}/studentplayground/api/requests/admin-registration-action/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              request_id: requestId,
              action: action
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          let successMessage;
          if (action === 'mark_handled') {
            successMessage = 'Request marked as handled successfully!';
          } else if (action === 'mark_unhandled') {
            successMessage = 'Request marked as unhandled successfully!';
          } else if (action === 'mark_display' || action === 'mark_dont_display') {
            successMessage = 'Successfully changed the teacher registration request display';
          } else {
            successMessage = `Request ${action} successfully!`;
          }
          alert(successMessage);
          // Refresh the requests list
          await handleFetchRequests();
        } else {
          const error = await response.json();
          console.error('Failed to perform action:', error);
          alert(`Failed to ${action} request: ${error.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error in admin action:', error);
        alert(`Error ${action}ing request: ${error.message}`);
      }
    }
  </script>
  
  <style>
  /* Header styling matching AdminCreateUserComponent */
  .labelDiv {
    display: flex;
    justify-content: space-around;
    margin-bottom: 2rem;
  }
  .labelDiv span {
    font-size: 1.2rem;
    font-weight: bold;
    color: #333;
  }

  /* Loading and empty states */
  .loading-message, .empty-message {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
  }

  /* Date group styling */
  .date-group {
    margin-bottom: 2rem;
  }
  .date-header {
    font-size: 1.1rem;
    font-weight: bold;
    color: #0055aa;
    border-bottom: 2px solid #0055aa;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  /* Section headers */
  .section-header {
    margin: 2rem 0 1rem 0;
    padding: 1.5rem;
    border-radius: 8px;
    text-align: center;
  }
  .section-header h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    font-weight: bold;
  }
  .section-header p {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.8;
  }
  .unhandled-section {
    background: linear-gradient(135deg, #fff3cd 0%, #fff8dc 100%);
    border: 1px solid #ffeaa7;
    color: #856404;
  }

  /* Handled requests section */
  .handled-section-container {
    margin-top: 3rem;
    border-top: 2px solid #e9ecef;
    padding-top: 2rem;
  }
  .handled-section-toggle {
    width: 100%;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid #dee2e6;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    transition: all 0.2s ease;
  }
  .handled-section-toggle:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  .toggle-text {
    font-size: 1.1rem;
    font-weight: bold;
    color: #495057;
  }
  .toggle-subtitle {
    font-size: 0.9rem;
    color: #6c757d;
    font-style: italic;
  }
  .handled-requests-content {
    margin-top: 1rem;
  }

  /* Handled request styling differences */
  .handled-date-group {
    opacity: 0.85;
  }
  .handled-date-header {
    color: #6c757d !important;
    border-bottom-color: #6c757d !important;
  }
  .handled-card {
    opacity: 0.9;
  }

  /* Request card styling */
  .request-card {
    border: 1px solid #ccc;
    border-radius: 8px;
    margin-bottom: 1rem;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
  }
  .request-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  /* Summary button styling */
  .request-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1rem;
    border: none;
    background: #fff;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;
    font-size: 1rem;
  }
  .request-summary:hover {
    background-color: #f8f9fa;
  }

  /* Status-based coloring */
  .request-summary.unhandled {
    border-left: 4px solid #ffc107;
  }
  .request-summary.handled {
    border-left: 4px solid #28a745;
  }

  /* Summary content layout */
  .summary-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    margin-right: 1rem;
  }
  .summary-main {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .student-name {
    font-weight: bold;
    color: #333;
    font-size: 1rem;
  }
  .subject-info {
    color: #666;
    font-size: 0.9rem;
  }

  /* Status badges container */
  .status-badges {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-end;
  }

  /* Status badge */
  .status-badge {
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .status-unhandled {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }
  .status-handled {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  /* Acceptance badge */
  .acceptance-badge {
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .acceptance-accepted {
    background-color: #e8f5e8;
    color: #2e7d32;
    border: 1px solid #c8e6c9;
  }
  .acceptance-not-accepted {
    background-color: #fff3e0;
    color: #ef6c00;
    border: 1px solid #ffcc02;
  }

  /* Display badge */
  .display-badge {
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .display-visible {
    background-color: #e1f5fe;
    color: #01579b;
    border: 1px solid #b3e5fc;
  }
  .display-hidden {
    background-color: #ffebee;
    color: #c62828;
    border: 1px solid #ffcdd2;
  }

  /* Expand icon */
  .expand-icon {
    color: #0055aa;
    font-size: 0.8rem;
    transition: transform 0.2s ease;
  }

  /* Details section */
  .request-details {
    padding: 1.5rem;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
  }
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .detail-item.full-width {
    grid-column: 1 / -1;
  }
  .detail-label {
    font-weight: bold;
    color: #0055aa;
    font-size: 0.9rem;
  }
  .detail-value {
    color: #333;
    font-size: 1rem;
    padding: 0.4rem;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  .topic-text {
    min-height: 3rem;
    resize: none;
    font-family: inherit;
  }

  /* Admin action buttons */
  .admin-actions {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    flex-wrap: wrap;
  }
  
  .action-button {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .handled-button {
    background-color: #28a745;
    color: white;
  }
  
  .handled-button:hover {
    background-color: #218838;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }
  
  .unhandled-button {
    background-color: #dc3545;
    color: white;
  }
  
  .unhandled-button:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }

  .show-button {
    background-color: #17a2b8;
    color: white;
  }
  
  .show-button:hover {
    background-color: #138496;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);
  }

  .hide-button {
    background-color: #6c757d;
    color: white;
  }
  
  .hide-button:hover {
    background-color: #5a6268;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(108, 117, 125, 0.3);
  }
  
  .action-button:active {
    transform: translateY(0);
  }

  /* Teacher accepted info styling */
  .teacher-accepted-info {
    background: #d4edda;
    color: #155724;
    padding: 0.8rem;
    border-radius: 6px;
    border: 1px solid #c3e6cb;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .teacher-accepted-info strong {
    font-weight: 600;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .details-grid {
      grid-template-columns: 1fr;
    }
    .summary-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    .status-badges {
      align-self: flex-end;
    }
    .admin-actions {
      flex-direction: column;
      gap: 0.5rem;
    }
    .action-button {
      justify-content: center;
    }
  }
</style>
  
  <div class="labelDiv">
  <span>CLASS REGISTRATION REQUESTS</span>
</div>

{#if loading}
  <div class="loading-message">Loading requests…</div>
{:else if Object.keys(groupedRequests).length === 0}
  <div class="empty-message">No class registration requests found.</div>
{:else}
  <!-- Now using reactive statements to separate requests -->

  <!-- UNHANDLED REQUESTS SECTION -->
  {#if Object.keys(unhandledRequestsByDay).length > 0}
    <div class="section-header unhandled-section">
      <h2>🔴 Unhandled Requests</h2>
      <p>These requests require your attention</p>
    </div>
    
    {#each Object.entries(unhandledRequestsByDay) as [day, requests]}
      <div class="date-group">
        <h3 class="date-header">{day}</h3>
        {#each requests as req (req.id)}
          <div class="request-card">
            <!-- Summary Header -->
            <button
              type="button"
              class="request-summary unhandled"
              on:click={() => toggleExpand(req.id)}
            >
              <div class="summary-content">
                <div class="summary-main">
                  <span class="student-name">{getStudentName(req.student)}</span>
                  <span class="subject-info">{getSubjectDisplayName(req.subject)} - {getGradeDisplayName(req.grade)}</span>
                </div>
                <div class="status-badges">
                  <div class="status-badge status-unhandled">Unhandled</div>
                  <div class="acceptance-badge" class:acceptance-accepted={req.accepted_by_teacher} 
                       class:acceptance-not-accepted={!req.accepted_by_teacher}>
                    {req.accepted_by_teacher ? 'Accepted' : 'Not Accepted'}
                  </div>
                  <div class="display-badge" class:display-visible={req.display} 
                       class:display-hidden={!req.display}>
                    {req.display ? 'Visible' : 'Hidden'}
                  </div>
                </div>
              </div>
              <div class="expand-icon">
                {expandedMap[req.id] ? '▼' : '▶'}
              </div>
            </button>

            <!-- Expanded Details -->
            {#if expandedMap[req.id]}
              <div class="request-details">
                <div class="details-grid">
                  <div class="detail-item">
                    <span class="detail-label">Request ID:</span>
                    <span class="detail-value">#{req.id}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Student:</span>
                    <span class="detail-value">{getStudentName(req.student)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Student Email:</span>
                    <span class="detail-value">{req.student_email || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Subject:</span>
                    <span class="detail-value">{getSubjectDisplayName(req.subject)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Grade Level:</span>
                    <span class="detail-value">{getGradeDisplayName(req.grade)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Submitted:</span>
                    <span class="detail-value">{formatDateTime(req.time_submitted)}</span>
                  </div>
                  <div class="detail-item full-width">
                    <span class="detail-label">Requested Times:</span>
                    <span class="detail-value">{formatRequestedTimes(req.requested_times)}</span>
                  </div>
                  <div class="detail-item full-width">
                    <span class="detail-label">Topic to Start With:</span>
                    <span class="detail-value topic-text">{req.topic_start || 'No topic specified'}</span>
                  </div>
                  
                  <!-- Teacher acceptance info -->
                  {#if req.accepted_by_teacher}
                    <div class="detail-item full-width">
                      <span class="detail-label">Teacher Acceptance:</span>
                      <div class="teacher-accepted-info">
                        <strong>✓ Accepted by:</strong> {req.accepted_by_teacher}<br>
                        <strong>Accepted at:</strong> {formatDateTime(req.teacher_accepted_at)}
                      </div>
                    </div>
                  {/if}
                  
                  <!-- Admin action buttons -->
                  <div class="detail-item full-width">
                    <span class="detail-label">Admin Actions:</span>
                    <div class="admin-actions">
                      <button 
                        class="action-button handled-button"
                        on:click={() => handleAdminAction(req.id, 'mark_handled')}
                      >
                        ✓ Mark as Handled
                      </button>
                      
                      {#if req.display}
                        <button 
                          class="action-button hide-button"
                          on:click={() => handleAdminAction(req.id, 'mark_dont_display')}
                        >
                          👁️‍🗨️ Hide from Teachers
                        </button>
                      {:else}
                        <button 
                          class="action-button show-button"
                          on:click={() => handleAdminAction(req.id, 'mark_display')}
                        >
                          👁️ Show to Teachers
                        </button>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  {:else}
    <div class="section-header unhandled-section">
      <h2>🟢 No Unhandled Requests</h2>
      <p>All requests have been handled!</p>
    </div>
  {/if}

  <!-- HANDLED REQUESTS SECTION -->
  {#if Object.keys(handledRequestsByDay).length > 0}
    <div class="handled-section-container">
      <button 
        class="handled-section-toggle"
        on:click={() => showHandledRequests = !showHandledRequests}
      >
        <span class="toggle-text">
          {showHandledRequests ? '▼' : '▶'} Handled Requests ({Object.values(handledRequestsByDay).flat().length})
        </span>
        <span class="toggle-subtitle">Click to {showHandledRequests ? 'hide' : 'show'} completed requests</span>
      </button>

      {#if showHandledRequests}
        <div class="handled-requests-content">
          {#each Object.entries(handledRequestsByDay) as [day, requests]}
            <div class="date-group handled-date-group">
              <h3 class="date-header handled-date-header">{day}</h3>
              {#each requests as req (req.id)}
                <div class="request-card handled-card">
                  <!-- Summary Header -->
                  <button
                    type="button"
                    class="request-summary handled"
                    on:click={() => toggleExpand(req.id)}
                  >
                    <div class="summary-content">
                      <div class="summary-main">
                        <span class="student-name">{getStudentName(req.student)}</span>
                        <span class="subject-info">{getSubjectDisplayName(req.subject)} - {getGradeDisplayName(req.grade)}</span>
                      </div>
                      <div class="status-badges">
                        <div class="status-badge status-handled">Handled</div>
                        <div class="acceptance-badge" class:acceptance-accepted={req.accepted_by_teacher} 
                             class:acceptance-not-accepted={!req.accepted_by_teacher}>
                          {req.accepted_by_teacher ? 'Accepted' : 'Not Accepted'}
                        </div>
                        <div class="display-badge" class:display-visible={req.display} 
                             class:display-hidden={!req.display}>
                          {req.display ? 'Visible' : 'Hidden'}
                        </div>
                      </div>
                    </div>
                    <div class="expand-icon">
                      {expandedMap[req.id] ? '▼' : '▶'}
                    </div>
                  </button>

                  <!-- Expanded Details -->
                  {#if expandedMap[req.id]}
                    <div class="request-details">
                      <div class="details-grid">
                        <div class="detail-item">
                          <span class="detail-label">Request ID:</span>
                          <span class="detail-value">#{req.id}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Student:</span>
                          <span class="detail-value">{getStudentName(req.student)}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Student Email:</span>
                          <span class="detail-value">{req.student_email || 'N/A'}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Subject:</span>
                          <span class="detail-value">{getSubjectDisplayName(req.subject)}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Grade Level:</span>
                          <span class="detail-value">{getGradeDisplayName(req.grade)}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">Submitted:</span>
                          <span class="detail-value">{formatDateTime(req.time_submitted)}</span>
                        </div>
                        <div class="detail-item full-width">
                          <span class="detail-label">Requested Times:</span>
                          <span class="detail-value">{formatRequestedTimes(req.requested_times)}</span>
                        </div>
                        <div class="detail-item full-width">
                          <span class="detail-label">Topic to Start With:</span>
                          <span class="detail-value topic-text">{req.topic_start || 'No topic specified'}</span>
                        </div>
                        
                        <!-- Teacher acceptance info -->
                        {#if req.accepted_by_teacher}
                          <div class="detail-item full-width">
                            <span class="detail-label">Teacher Acceptance:</span>
                            <div class="teacher-accepted-info">
                              <strong>✓ Accepted by:</strong> {req.accepted_by_teacher}<br>
                              <strong>Accepted at:</strong> {formatDateTime(req.teacher_accepted_at)}
                            </div>
                          </div>
                        {/if}
                        
                        <!-- Admin action buttons -->
                        <div class="detail-item full-width">
                          <span class="detail-label">Admin Actions:</span>
                          <div class="admin-actions">
                            <button 
                              class="action-button unhandled-button"
                              on:click={() => handleAdminAction(req.id, 'mark_unhandled')}
                            >
                              ✗ Mark as Unhandled
                            </button>
                            
                            {#if req.display}
                              <button 
                                class="action-button hide-button"
                                on:click={() => handleAdminAction(req.id, 'mark_dont_display')}
                              >
                                👁️‍🗨️ Hide from Teachers
                              </button>
                            {:else}
                              <button 
                                class="action-button show-button"
                                on:click={() => handleAdminAction(req.id, 'mark_display')}
                              >
                                👁️ Show to Teachers
                              </button>
                            {/if}
                          </div>
                        </div>
                      </div>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
{/if}
  
